import type { BlockDefinition, BlockRootContext, BlockSerialized, BlockSerializedChild } from '#layers/block-engine/types/block';
import { useRoute, useRouter } from '#app/composables/router';
import { noop } from '#layers/base/public';
import { blockExpressionMarker, parseBlockExpression, useBlockExpressionEval } from '#layers/block-engine/utils/block/expression';
import { useBlockInjection } from '#layers/block-engine/utils/block/inject';

export interface UseBlockRenderPropsOptions {
	/**
	 * Variable values (name => value).
	 */
	variables: Record<string, any>;
	/**
	 * Variable id to name mapping.
	 */
	variableIdToName: Map<string, string>;
	/**
	 * Context passed to the root block.
	 */
	getRootContext: () => BlockRootContext;
	/**
	 * Used to trigger events.
	 */
	emit?: (event: string, payload?: any) => void;
	/**
	 * Don't wrap the block event handlers with the standard payload.
	 */
	unwrapBlockEvents?: boolean;
}

export interface GetPropsOptions {
	child: BlockSerializedChild;
	childBlock?: BlockSerialized | BlockDefinition;
}

export interface BlockEventFunctionPayload {
	/**
	 * First event argument.
	 */
	event: any;
	/**
	 * Rest of the event arguments.
	 */
	args: any[];
	route: ReturnType<typeof useRoute>;
	router: ReturnType<typeof useRouter>;
	inject: ReturnType<typeof useBlockInjection>['unwrappedInjection'];
	variables: Record<string, any>;
	getRootContext: () => BlockRootContext;
	getVariableById: (id: string) => any;
	setVariableById: (id: string, value: any) => void;
	/**
	 * Emit an event from the root block.
	 */
	emit: (event: string, payload?: any) => void;
}

export function useBlockRenderProps(options: UseBlockRenderPropsOptions) {
	const {
		variables,
		variableIdToName,
		getRootContext,
		emit = () => {},
		unwrapBlockEvents,
	} = options;

	/**
	 * Expression => Function
	 *
	 * Cache creation of functions to improve performance.
	 * Especially useful when eval is involved.
	 */
	const propFunctionCache = new Map<string, any>();

	/**
	 * Cache the model event expression
	 */
	const modelPropsCache = new Map<string, { eventPropKey: string; expression: string }>();

	const $route = useRoute();
	const $router = useRouter();
	const { unwrappedInjection } = useBlockInjection();
	const $inject = unwrappedInjection;
	const $emit = emit;

	const { compileExpression } = useBlockExpressionEval({
		variables,
		variableIdToName,
		additionalApi: {
			$route,
			$router,
			$inject,
			$emit,
			get $context() {
				return getRootContext();
			},
		},
	});

	function getVariableById(id: string) {
		const name = variableIdToName.get(id);

		if (!name) {
			throw new Error(`Variable with id ${id} not found`);
		}

		return variables[name];
	}

	function setVariableById(id: string, value: any) {
		const name = variableIdToName.get(id);

		if (!name) {
			throw new Error(`Variable with id ${id} not found`);
		}

		variables[name] = value;
	}

	function getPropValue(childBlock: BlockSerialized | BlockDefinition | undefined, propKey: string, value: any) {
		// Events
		if (/^on[A-Z]/.test(propKey) && value) {
			if (typeof value === 'string') {
				let expression = value;

				let fn: (...args: any[]) => any;

				if (!propFunctionCache.has(expression)) {
					if (import.meta.client) {
						// Rewrite variables access to allow mutation
						for (const variableName of Object.keys(variables)) {
							expression = expression.replaceAll(new RegExp(String.raw`\b${variableName}\b`, 'g'), `variables['${variableName}']`);
						}

						for (const [variableId, variableName] of variableIdToName) {
							expression = expression.replaceAll(new RegExp(String.raw`\b${variableId}\b`, 'g'), `variables['${variableName}']`);
						}

						// Payload arguments
						const originalEventName = propKey.replace(/^on([A-Z])/, (m, p1) => p1.toLowerCase());
						const payload = childBlock?.events?.find((e) => e.name === propKey || e.name === originalEventName)?.payload ?? [];

						const api = {
							variables,
							getVariableById,
							setVariableById,
							$route,
							$router,
							$inject,
							$emit,
							get $context() {
								return getRootContext();
							},
						};

						const fnBody = `return (${payload.map((p) => p.name).join(',')}) => {const $event = ${payload[0]?.name ?? 'undefined'};${expression};}`;

						try {
							// eslint-disable-next-line no-new-func
							fn = new Function(...Object.keys(api), fnBody) as (...args: any[]) => any;
							fn = fn(...Object.values(api));
						}
						catch (error: any) {
							console.warn(fnBody);
							console.error(error);
							fn = noop;
						}
					}
					else {
						// Skip eval on server
						fn = noop;
					}

					propFunctionCache.set(expression, fn);
				}
				else {
					fn = propFunctionCache.get(expression);
				}

				return fn;
			}
			else if (typeof value === 'function') {
				if (unwrapBlockEvents) {
					return value;
				}

				return (event: any, ...args: any[]) => value({
					event,
					args,
					route: $route,
					router: $router,
					inject: $inject,
					variables,
					getRootContext,
					getVariableById,
					setVariableById,
					emit,
				} satisfies BlockEventFunctionPayload);
			}
			else {
				// TODO frontend flow
				return value;
			}
		}
		// Expression
		else if (typeof value === 'string' && value.startsWith(blockExpressionMarker)) {
			value = value.slice(blockExpressionMarker.length).trim();

			// Variable name
			let fn: (...args: any[]) => any;

			if (!propFunctionCache.has(value)) {
				fn = compileExpression(value);
				propFunctionCache.set(value, fn);
			}
			else {
				fn = propFunctionCache.get(value);
			}

			value = fn();
		}

		// Not an expression
		return value;
	}

	function getProps(options: GetPropsOptions) {
		const result: Record<string, any> = {};

		for (const [key, value] of Object.entries(options.child.props)) {
			// Skip event if it's an update event for a model prop that is already bound with v-model
			if (/^on[A-Z]/.test(key)) {
				const modelName = /^onUpdate:(.+)$/.exec(key)?.[1];

				if (modelName && options.child.modelProps?.includes(modelName)) {
					continue;
				}
			}

			result[key] = getPropValue(options.childBlock, key, value);

			// Add v-model event
			if (options.child.modelProps?.includes(key)) {
				try {
					let expression: string;
					let eventPropKey: string;
					const cacheKey = `${options.child.id}:${key}`;

					if (!modelPropsCache.has(cacheKey)) {
						const modelEvent = `update:${key}`;
						eventPropKey = `on${modelEvent[0]!.toUpperCase()}${modelEvent.slice(1)}`;
						const parsed = parseBlockExpression(value);
						const expNode = parsed.find((n) => n.type === 'expression');

						if (!expNode) {
							throw new Error(`Expected expression in prop "${key}" but got: ${value}`);
						}

						if (expNode.child.type !== 'prop') {
							throw new Error(`Expected property access in prop "${key}" but got: ${value}`);
						}

						const assignedTo = expNode.child.path.map((p, index) => index === 0 ? p : `[${JSON.stringify(p)}]`).join('');
						expression = `${assignedTo} = $event`;
						modelPropsCache.set(key, { eventPropKey, expression });
					}
					else {
						({ eventPropKey, expression } = modelPropsCache.get(cacheKey)!);
					}

					result[eventPropKey] = getPropValue(options.childBlock, eventPropKey, expression);
				}
				catch (error) {
					console.warn(error);
				}
			}
		}

		return result;
	}

	return {
		getProps,
	};
}
